import { useCallback, useMemo, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { toast } from 'sonner';
import { useDisconnect } from 'wagmi';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SidebarTrigger,
  useSidebar,
} from '@gu-corp/gu-ui';
import {
  Sun,
  Moon,
  Monitor,
  ChevronDown,
  Globe,
  Check,
  Copy,
  Link2Off,
  Wallet,
} from 'lucide-react';
import { useModal } from 'connectkit';

import Identicon from '~/components/identicon';
import { useTheme } from '~/components/theme-provider';
import { AppRoutesEnum } from '~/enums/route.enum';
import { truncateEthAddress } from '~/utils/string.utils';

const languageOptions = [
  { value: 'en', labelKey: 'english' },
  { value: 'ja', labelKey: 'japanese' },
  { value: 'ar', labelKey: 'arabic' },
  { value: 'de', labelKey: 'german' },
  { value: 'es', labelKey: 'spanish' },
  { value: 'fr', labelKey: 'french' },
  { value: 'hi', labelKey: 'hindi' },
  { value: 'id', labelKey: 'indonesian' },
  { value: 'it', labelKey: 'italian' },
  { value: 'ko', labelKey: 'korean' },
  { value: 'pt', labelKey: 'portuguese' },
  { value: 'ru', labelKey: 'russian' },
  { value: 'th', labelKey: 'thai' },
  { value: 'tr', labelKey: 'turkish' },
  { value: 'vi', labelKey: 'vietnamese' },
  { value: 'zh-CN', labelKey: 'chinese_simplified' },
  { value: 'zh-TW', labelKey: 'chinese_traditional' },
] as const;

const getLanguageDisplay = (lang: string) => {
  const displays: Record<string, string> = {
    en: 'EN',
    ja: '日本語',
    ar: 'العربية',
    de: 'DE',
    es: 'ES',
    fr: 'FR',
    hi: 'हिंदी',
    id: 'ID',
    it: 'IT',
    ko: '한국어',
    pt: 'PT',
    ru: 'RU',
    th: 'ไทย',
    tr: 'TR',
    vi: 'VI',
    'zh-CN': '简体中文',
    'zh-TW': '繁體中文',
  };
  return displays[lang] || lang.toUpperCase();
};

// Translation keys for page names
const pageNameKeys: Record<string, string> = {
  '': 'dashboard',
  wallet: 'wallet',
  nfts: 'nft',
  lending: 'lending',
  ecosystem: 'ecosystem',
  development: 'development',
  contact: 'contact',
  'nft-detail': 'nft',
};

// Pages with dynamic routes that should show simplified breadcrumbs
// Maps pathname pattern to simplified breadcrumb config
const simplifiedBreadcrumbs: Record<
  string,
  { parent: string; parentKey: string; labelKey: string }
> = {
  '/nfts/[contractAddress]/[tokenId]': {
    parent: '/nfts',
    parentKey: 'nft',
    labelKey: 'nft_detail',
  },
};

// Navigation items for mobile dropdown
const navItemKeys = [
  { href: AppRoutesEnum.DASHBOARD, labelKey: 'dashboard' },
  { href: AppRoutesEnum.WALLET, labelKey: 'wallet' },
  { href: AppRoutesEnum.NFTS, labelKey: 'nft' },
  { href: AppRoutesEnum.LENDING, labelKey: 'lending' },
  { href: AppRoutesEnum.ECOSYSTEM, labelKey: 'ecosystem' },
  { href: AppRoutesEnum.DEVELOPMENT, labelKey: 'development' },
];

interface HeaderProps {
  walletAddress?: string;
}

const MOBILE_BREAKPOINT = 768;

export function Header({ walletAddress }: HeaderProps) {
  const { theme, setTheme } = useTheme();
  const { i18n, t } = useTranslation();
  const router = useRouter();
  const pathname = router.pathname;
  const query = router.query;
  const { open: sidebarOpen } = useSidebar();
  const [isMobile, setIsMobile] = useState(false);
  const [walletPopoverOpen, setWalletPopoverOpen] = useState(false);
  const { disconnect } = useDisconnect();
  const { setOpen: setConnectModalOpen } = useModal();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const showDropdownNav = !sidebarOpen || isMobile;

  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const currentLanguage = i18n.language;

  const onLanguageChange = useCallback(
    (languageCode: string) => {
      i18n.changeLanguage(languageCode);
      localStorage.setItem('language', languageCode);
      // Update URL with new locale to persist across navigation
      router.push(router.asPath, router.asPath, { locale: languageCode });
    },
    [i18n, router],
  );

  const handleCopyAddress = useCallback(async () => {
    if (!walletAddress) return;
    try {
      await navigator.clipboard.writeText(walletAddress);
      toast.success(t('copied'));
      setWalletPopoverOpen(false);
    } catch {
      toast.error('Failed to copy');
    }
  }, [walletAddress, t]);

  const handleDisconnect = useCallback(() => {
    disconnect();
    setWalletPopoverOpen(false);
  }, [disconnect]);

  const handleConnectWallet = useCallback(() => {
    setConnectModalOpen(true);
  }, [setConnectModalOpen]);

  // Parse pathname into breadcrumb items
  const segments = pathname.split('/').filter(Boolean);

  const breadcrumbs = useMemo(() => {
    // Helper to get translated page name
    const getPageName = (page: string): string => {
      const key = pageNameKeys[page];
      return key ? t(key) : page;
    };

    const items: { label: string; href?: string }[] = [];

    // Check for simplified breadcrumb patterns
    const simplifiedConfig = simplifiedBreadcrumbs[pathname];
    if (simplifiedConfig) {
      items.push({ label: t('dashboard'), href: '/' });
      items.push({ label: t(simplifiedConfig.parentKey), href: simplifiedConfig.parent });
      items.push({ label: t(simplifiedConfig.labelKey) });
      return items;
    }

    if (segments.length === 0) {
      items.push({ label: t('dashboard') });
    } else {
      items.push({ label: t('dashboard'), href: '/' });

      const mainPage = segments[0];
      const mainPageName = getPageName(mainPage);

      if (segments.length === 1) {
        items.push({ label: mainPageName });
      } else {
        items.push({ label: mainPageName, href: `/${mainPage}` });
        // Add remaining segments
        for (let i = 1; i < segments.length; i++) {
          const isLast = i === segments.length - 1;
          let segment = segments[i];

          // Handle dynamic route segments like [category]
          if (segment.startsWith('[') && segment.endsWith(']')) {
            const paramName = segment.slice(1, -1); // Remove brackets
            segment = (query[paramName] as string) || segment;
          }

          // Special handling for ecosystem category segments
          let segmentLabel = segment;
          if (mainPage === 'ecosystem' && i === 1) {
            // Translate ecosystem categories using ecosystem_page.{category} pattern
            segmentLabel = t(`ecosystem_page.${segment}`);
          }

          if (isLast) {
            items.push({ label: segmentLabel });
          } else {
            items.push({ label: segmentLabel, href: '/' + segments.slice(0, i + 1).join('/') });
          }
        }
      }
    }

    return items;
  }, [segments, pathname, query, t]);

  // Current page label for mobile
  const currentPageLabel = breadcrumbs[breadcrumbs.length - 1]?.label || t('dashboard');

  return (
    <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-border bg-background px-4">
      <div className="flex items-center gap-2">
        <SidebarTrigger className="-ml-1 text-foreground" data-testid="sidebar-trigger" />
        <div className="h-4 w-px bg-border" />

        {/* Dropdown navigation (shown when sidebar is closed or on mobile) */}
        {showDropdownNav && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1 px-2 text-foreground"
                data-testid="nav-dropdown-trigger"
              >
                <span className="text-sm font-medium">{currentPageLabel}</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {navItemKeys.map((item) => (
                <DropdownMenuItem
                  key={item.href}
                  onClick={() => router.push(item.href, item.href, { locale: router.locale })}
                  className={
                    pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href))
                      ? 'font-medium'
                      : ''
                  }
                >
                  {t(item.labelKey)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Breadcrumb navigation (shown when sidebar is open and not on mobile) */}
        {!showDropdownNav && (
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumbs.flatMap((crumb, index) => {
                const elements: React.ReactNode[] = [];
                if (index > 0) {
                  elements.push(
                    <BreadcrumbSeparator key={`sep-${index}`} className="!text-foreground" />,
                  );
                }
                elements.push(
                  <BreadcrumbItem key={`item-${index}`}>
                    {crumb.href ? (
                      <BreadcrumbLink
                        onClick={() =>
                          router.push(crumb.href!, crumb.href!, { locale: router.locale })
                        }
                        className="cursor-pointer !text-foreground hover:!text-foreground"
                      >
                        {crumb.label}
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage className="!text-foreground">{crumb.label}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>,
                );
                return elements;
              })}
            </BreadcrumbList>
          </Breadcrumb>
        )}
      </div>

      <div className="flex items-center gap-1">
        {/* Language Selector */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2 text-foreground"
              data-testid="language-selector"
            >
              <Globe className="h-4 w-4" />
              <span className="text-xs hidden sm:inline">
                {getLanguageDisplay(currentLanguage)}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-popover text-popover-foreground max-h-80 overflow-y-auto"
          >
            {languageOptions.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => onLanguageChange(option.value)}
                className="flex items-center justify-between"
              >
                <span>{t(option.labelKey)}</span>
                {currentLanguage === option.value && <Check className="h-4 w-4 ml-2" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme Selector */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-foreground"
              data-testid="theme-selector"
            >
              <ThemeIcon className="h-4 w-4" />
              <span className="sr-only">{t('theme')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover text-popover-foreground">
            <DropdownMenuItem onClick={() => setTheme('light')} data-testid="theme-light">
              <Sun className="mr-2 h-4 w-4" />
              {t('theme_light')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('dark')} data-testid="theme-dark">
              <Moon className="mr-2 h-4 w-4" />
              {t('theme_dark')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('system')} data-testid="theme-system">
              <Monitor className="mr-2 h-4 w-4" />
              {t('theme_system')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Separator */}
        <div className="h-4 w-px bg-border mx-1" />

        {/* Wallet Connection */}
        {walletAddress ? (
          <Popover open={walletPopoverOpen} onOpenChange={setWalletPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 px-2 gap-2 hover:bg-accent text-foreground"
                data-testid="wallet-button"
              >
                <Identicon address={walletAddress} size={20} />
                <span className="text-sm font-medium hidden sm:inline">
                  {truncateEthAddress(walletAddress)}
                </span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-36 !p-1" align="end" side="bottom" sideOffset={4}>
              <div
                className="flex items-center p-2 gap-2 rounded-md cursor-pointer hover:bg-accent text-foreground"
                onClick={handleCopyAddress}
                data-testid="copy-address-button"
              >
                <Copy className="w-4 h-4" />
                <span className="text-sm">{t('copy')}</span>
              </div>
              <div
                className="flex items-center p-2 gap-2 rounded-md cursor-pointer hover:bg-destructive/10"
                onClick={handleDisconnect}
                data-testid="disconnect-button"
              >
                <Link2Off className="w-4 h-4 text-destructive" />
                <span className="text-destructive text-sm">{t('disconnect')}</span>
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-2"
            onClick={handleConnectWallet}
            data-testid="connect-wallet-button"
          >
            <Wallet className="h-4 w-4" />
            <span className="hidden sm:inline">{t('connect_wallet')}</span>
          </Button>
        )}
      </div>
    </header>
  );
}

export default Header;
